/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, Component } from '@angular/core';
import { DashboardDataModel, WidgetModel } from '../../../../models';
import { DashboardService } from '../../../../services/component';
import { UtilsService } from '../../../../../../../core/services';
import { WidgetComponent } from '../widgetBaseComponent/widget.component';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-user-widget',
    templateUrl: '../widgetBaseComponent/widget.component.html',
    styleUrls: ['../widgetBaseComponent/widget.component.scss']
})

export class UserWidgetComponent extends WidgetComponent implements AfterViewInit {
    constructor(
        dashboardService: DashboardService,
        utilsService: UtilsService,
    ) {
        super(dashboardService, utilsService);

        const data: DashboardDataModel = this.dashboardService.allData;
        this.setUpPage(data);
    }

    ngAfterViewInit(): void {
        this.dashboardService.allDataEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                data => {
                    this.setUpPage(data);
                }
            );
    }

    /**
     * Set up page.
     * @param data
     * @private
     */
    private setUpPage(data: any): void {
        this.view = null;
        const adcDetails: any = this.formatUserData(data.users);
        this.view = this.getUserViewData(adcDetails);
        this.loaded = true;
    }

    /**
     * Format user data.
     * @private
     * @param users
     */
    private formatUserData(users: any[] = []): any {
        const pending: number = users.filter(item => !item.activated).length;
        const total: number = users.length - pending;

        return { total, pending };
    }

    /**
     * Get user data for the view.
     * @param userDetails
     * @private
     */
    private getUserViewData(userDetails: { total: any; pending: any; }): any {
        let viewObject: WidgetModel = this.getBaseMessaging(this.baseView);

        const error: any = this.errors?.users;
        if (error) {
            return this.getErrorMessaging(viewObject, error);
        }

        if (userDetails.total > 1 || userDetails.pending > 1) {
            viewObject = this.getActiveMessaging(viewObject, userDetails);
        }

        return viewObject;
    }

    /**
     * Get base view messaging.
     * @param baseView
     * @private
     */
    private getBaseMessaging(baseView: WidgetModel): WidgetModel {
        const baseData = {
            iconSrc: 'assets/centralisedManagement/icons/users.svg',
            title: 'users',
            flagState: 'success',
            subtitle: 'Invite users',
            message: 'You haven\'t added any users to your organization yet.',
            linkText: 'Invite user',
            linkUrl: '/cm/organisation/users/user/add',
        };

        return this.updateObject(baseView, baseData);
    }

    /**
     * Get active messaging.
     * @param viewObject
     * @param userDetails
     * @private
     */
    private getActiveMessaging(viewObject: WidgetModel, userDetails: { total: any; pending: any; }): WidgetModel {
        const userText: 'users' | 'user' = userDetails.total === 1 ? 'user' : 'users';
        const newData = {
            flagState: 'null',
            subtitle: null,
            message: `You have ${userDetails.total} active ${userText} and ${userDetails.pending} pending.`,
            linkText: 'Manage my users',
            linkUrl: '/cm/organisation/users',
            counters: [
                {
                    count: `${userDetails.total}`,
                    countSubTitle: 'Active',
                    subTitleClass: 'success'
                },
                {
                    count: `${userDetails.pending}`,
                    countSubTitle: 'Pending',
                    subTitleClass: 'lighter-grey small'
                }
            ]
        };

        return this.updateObject(viewObject, newData);
    }
}
